import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai/index';
import styles from './css/index.module.scss';
import ListSearch from '../ListSearch';
import useTranslations from '../../../../hooks/useTranslations';
import { DICTIONARY } from '../../../../translations/dictionary';
import Checkbox from '../../../../ui-kit/Checkbox';
import IconButton from '../../../../ui-kit/IconButton';
import Api from '../../../../api';
import sendRequest from '../../../../helpers/sendRequest';
import { openInfoModalAtom } from '../../../../atoms/modalAtoms';

import AddIcon from '../../../../assets/icons/add.svg';
import ArchiveIcon from '../../../../assets/icons/download.svg';
import DockIcon from '../../../../assets/icons/document.svg';
import EmailIcon from '../../../../assets/icons/email.svg';
import CrossIcon from '../../../../assets/icons/crossDark.svg';
import { isAdminAtom } from '../../../../atoms/accountAtoms';
import { windowWidthAtom } from '../../../../atoms/globalAtoms';
import useProjects from '../../../../hooks/useProjects';
import useConfigurations from '../../../../hooks/useConfigurations';

// Up to these window widths the action buttons show only icons, so the row with the search fits.
const ICON_ONLY_MAX_WIDTH_PROJECTS = 1030;
const ICON_ONLY_MAX_WIDTH_HISTORY = 1180;

function ProjectsSelectingButtons({
  allItems,
  selectedItems,
  setSelectedItems,
  isForProjects,
  isForHistory,
}) {
  const { t } = useTranslations();

  const {
    onDeleteProjectsClick,
    downloadProjectsArchive,
    downloadProjectsFile,
    emailProjectsArchive,
    searchProjects,
  } = useProjects({ setSelectedItems });

  const {
    downloadConfigurationsArchive,
    downloadConfigurationsFile,
    emailConfigurationsArchive,
    onAddToProjectClick,
    onDeleteConfigurationsClick,
    searchConfigurations,
  } = useConfigurations({ setSelectedItems });

  const [isAdmin] = useAtom(isAdminAtom);
  const [windowWidth] = useAtom(windowWidthAtom);
  // Not inside one project: there the row has no search and fits as it is.
  const isIconOnly = (isForProjects && windowWidth <= ICON_ONLY_MAX_WIDTH_PROJECTS)
    || (isForHistory && windowWidth <= ICON_ONLY_MAX_WIDTH_HISTORY);

  const onArchiveClick = async () => {
    if (isForProjects) await downloadProjectsArchive(selectedItems);
    else await downloadConfigurationsArchive(selectedItems);
  };

  const onFileClick = async () => {
    if (isForProjects) await downloadProjectsFile(selectedItems);
    else await downloadConfigurationsFile(selectedItems);
  };

  const onEmailClick = async () => {
    if (isForProjects) await emailProjectsArchive(selectedItems);
    else await emailConfigurationsArchive(selectedItems);
  };

  const onDeleteClick = async () => {
    if (isForProjects) onDeleteProjectsClick(selectedItems);
    else if (isForHistory) onDeleteConfigurationsClick(selectedItems);
  };

  const onSelectAllClick = () => {
    if (selectedItems.length) setSelectedItems([]);
    else setSelectedItems(allItems.map((i) => i.id));
  };

  const showSearch = isForProjects || isForHistory;

  const onSearch = (query) => {
    if (isForProjects) searchProjects(query);
    else if (isForHistory) searchConfigurations(query);
  };

  const onAddSelectedToProject = () => {
    const selected = allItems.filter((item) => selectedItems.includes(item.id));
    onAddToProjectClick(selected);
  };

  const actionButtons = (
    <>
      {isForHistory && (
        <IconButton
          onClick={onAddSelectedToProject}
          isDisabled={!selectedItems.length}
          text={t(DICTIONARY.addToProject)}
          icon={AddIcon}
          hideTextOnMobile
          isIconOnly={isIconOnly}
        />
      )}
      <IconButton
        onClick={onArchiveClick}
        isDisabled={!selectedItems.length}
        text={t(DICTIONARY.saveToArchive)}
        icon={ArchiveIcon}
        hideTextOnMobile
        isIconOnly={isIconOnly}
      />
      <IconButton
        onClick={onFileClick}
        isDisabled={!selectedItems.length}
        text={t(DICTIONARY.saveToDocx)}
        icon={DockIcon}
        hideTextOnMobile
        isIconOnly={isIconOnly}
      />
      <IconButton
        onClick={onEmailClick}
        isDisabled={!selectedItems.length}
        text={t(DICTIONARY.sendByEmail)}
        icon={EmailIcon}
        hideTextOnMobile
        isIconOnly={isIconOnly}
      />
      {(isForProjects || isForHistory) && (
        <IconButton
          onClick={onDeleteClick}
          isDisabled={!selectedItems.length}
          text={t(DICTIONARY.delete)}
          icon={CrossIcon}
          hideTextOnMobile
          isIconOnly={isIconOnly}
        />
      )}
    </>
  );

  return (
    <div className={classNames(styles.buttonWrapper, { [styles.withSearch]: showSearch })}>
      <div className={styles.checkbox}>
        <Checkbox
          onClick={onSelectAllClick}
          text={`${t(DICTIONARY.selected)} ${selectedItems.length}`}
          value={selectedItems.length}
        />
      </div>
      {showSearch && (
        <div className={styles.searchSlot}>
          <ListSearch
            onSearch={onSearch}
            hintText={t(isForProjects ? DICTIONARY.projectsSearchHint : DICTIONARY.configurationsSearchHint)}
          />
        </div>
      )}
      {showSearch ? <div className={styles.actions}>{actionButtons}</div> : actionButtons}
    </div>
  );
}

export default ProjectsSelectingButtons;
